/**
 * Strings.
 *
 * English and Puerto Rican Spanish are both first-class from the first commit.
 * Retrofitting i18n means finding every string already inlined in a component,
 * and on this account that has meant a translation layer that only ever covered
 * the pages someone remembered.
 *
 * `es-PR`, not generic `es`: the wording is Puerto Rican, and the distinction
 * matters to the people reading it.
 *
 * ── House rule for every component ───────────────────────────────────────────
 *
 * NEVER build an array of `t()` results at module scope. `Admin.tsx` evaluates
 * `t()` inside a module-level `TABS` const, which is why those labels freeze at
 * whatever locale the bundle first loaded under. Call `t(key)` during render.
 */

import { useSyncExternalStore } from 'react'

export type Locale = 'en' | 'es-PR'

/**
 * Exported for one reason: a test asserts that the `en` and `es-PR` key sets are
 * IDENTICAL and that neither holds an empty value. A missing translation is then
 * a build failure rather than an English sentence sitting in a Spanish UI, which
 * is the failure mode every half-retrofitted i18n layer on this account has had.
 */
export const STRINGS = {
  en: {
    'app.signIn': 'Sign in',
    'app.signOut': 'Sign out',
    'app.loading': 'Loading…',

    'auth.handle': 'Username',
    'auth.displayName': 'Your name',
    'auth.createAccount': 'Create account',
    'auth.usePasskey': 'Sign in with a passkey',
    'auth.noPasskeySupport': 'This browser cannot use passkeys. Try Chrome, Safari or Edge.',
    'auth.setupToken': 'Setup token',
    'auth.cancelled': 'That was cancelled. Nothing has changed.',

    'account.passkeys': 'Passkeys',
    'account.addPasskey': 'Add a passkey',
    'account.removePasskey': 'Remove',
    'account.synced': 'Synced to your password manager',
    'account.deviceOnly': 'This device only',
    'account.neverUsed': 'Never used',
    'account.lastUsed': 'Last used',
    'account.recoveryWarning':
      'This account has one passkey and it is not synced. Lose that device and the account cannot be recovered — add a second passkey.',

    'notify.enable': 'Turn on notifications',
    'notify.enabled': 'Notifications are on',
    'notify.blocked': 'Notifications are blocked in your browser settings.',
    'notify.inbox': 'Notifications',
    'notify.empty': 'Nothing yet.',

    'admin.title': 'Admin',
    'admin.users': 'Users',
    'admin.audit': 'Activity',
    'admin.errors': 'Errors',
    'admin.flags': 'Flags',
    'admin.settings': 'Settings',
    'admin.broadcast': 'Broadcast',
    'admin.devices': 'Devices',

    'env.beta': 'BETA — this is not the live app',
    'env.local': 'LOCAL',

    'error.generic': 'Something went wrong.',
    'error.offline': 'You appear to be offline.',

    'update.ready': 'A newer version is ready',
    'update.now': 'Update now',
    'update.later': 'Later',

    'install.title': 'Put this on your home screen',
    'install.why': 'It gets its own icon and opens without the browser bar.',
    'install.ios': 'Tap Share, then “Add to Home Screen”.',
    'install.add': 'Add it',
    'install.no': 'No thanks',

    /* ── The shell: the five tabs, the header, and the two states a frame
          owns on its own — nothing here, and nothing built here yet. ─────── */
    'nav.primary': 'Main navigation',
    'nav.hunt': 'Hunt',
    'nav.board': 'Board',
    'nav.add': 'Add',
    'nav.map': 'Map',
    'nav.me': 'Me',

    'shell.search': 'Search',
    'shell.alerts': 'Alerts',
    'shell.back': 'Back',
    'shell.unread': 'unread',
    'shell.account': 'Account',
    'shell.language': 'Language',
    'shell.skipToContent': 'Skip to content',
    'shell.notFound': 'That page does not exist.',
    'shell.notFoundBody': 'The link may be old, or the opportunity may have been merged into another one.',
    'shell.goHunt': 'Back to the hunt',
    'shell.pending': 'Not built yet',
    'shell.pendingBody': 'This screen arrives with the screens work unit. The frame around it is finished.',

    'route.opportunity': 'Opportunity',
    'route.inspect': 'Inspection',
    'route.compare': 'Compare',
    'route.intake': 'Add a lead',
    'route.setup': 'Setup',
    'route.settings': 'Settings',

    /* ── Page headers ─────────────────────────────────────────────────────
          The eyebrow says what KIND of screen this is, which is what makes a
          deep link legible when somebody arrives on it from a notification
          rather than from the navigation. */
    'page.eyebrowPipeline': 'Pipeline',
    'page.eyebrowGround': 'On the ground',
    'page.eyebrowDecide': 'Deciding',
    'page.eyebrowCapture': 'Capture',
    'page.eyebrowFind': 'Find',
    'page.eyebrowWatching': 'While you were away',
    'page.eyebrowYours': 'Yours',
    'page.boardLede': 'Every lead you are working, in the column for the stage it has reached.',
    'page.mapLede': 'Where the land actually is. Pin colour is the score.',
    'page.compareLede': 'Two to four parcels side by side, on the numbers that decide.',
    'page.inspectLede': 'The checklist you run standing on the parcel. It keeps working with no signal.',
    'page.intakeLede': 'Paste a link, or type what is in front of you. Under a minute at the gate.',
    'page.inboxLede': 'Price drops, listings pulled, dates coming due, leads gone quiet.',
    'page.settingsLede': 'Everything this application can show you is a switch here. Each one says what it does.',
  },

  'es-PR': {
    'app.signIn': 'Entrar',
    'app.signOut': 'Salir',
    'app.loading': 'Cargando…',

    'auth.handle': 'Usuario',
    'auth.displayName': 'Tu nombre',
    'auth.createAccount': 'Crear cuenta',
    'auth.usePasskey': 'Entrar con passkey',
    'auth.noPasskeySupport': 'Este navegador no puede usar passkeys. Prueba Chrome, Safari o Edge.',
    'auth.setupToken': 'Código de configuración',
    'auth.cancelled': 'Se canceló. No cambió nada.',

    'account.passkeys': 'Passkeys',
    'account.addPasskey': 'Añadir un passkey',
    'account.removePasskey': 'Quitar',
    'account.synced': 'Sincronizado con tu gestor de contraseñas',
    'account.deviceOnly': 'Solo en este equipo',
    'account.neverUsed': 'Nunca se ha usado',
    'account.lastUsed': 'Último uso',
    'account.recoveryWarning':
      'Esta cuenta tiene un solo passkey y no está sincronizado. Si pierdes ese equipo, no se puede recuperar la cuenta — añade un segundo passkey.',

    'notify.enable': 'Activar notificaciones',
    'notify.enabled': 'Las notificaciones están activadas',
    'notify.blocked': 'Las notificaciones están bloqueadas en tu navegador.',
    'notify.inbox': 'Notificaciones',
    'notify.empty': 'Nada todavía.',

    'admin.title': 'Administración',
    'admin.users': 'Usuarios',
    'admin.audit': 'Actividad',
    'admin.errors': 'Errores',
    'admin.flags': 'Flags',
    'admin.settings': 'Ajustes',
    'admin.broadcast': 'Mensaje',
    'admin.devices': 'Equipos',

    'env.beta': 'BETA — esta no es la app en vivo',
    'env.local': 'LOCAL',

    'error.generic': 'Algo salió mal.',
    'error.offline': 'Parece que no tienes conexión.',

    'update.ready': 'Hay una versión más nueva',
    'update.now': 'Actualizar',
    'update.later': 'Ahora no',

    'install.title': 'Ponlo en tu pantalla de inicio',
    'install.why': 'Tiene su propio ícono y abre sin la barra del navegador.',
    'install.ios': 'Toca Compartir y luego “Añadir a inicio”.',
    'install.add': 'Añadir',
    'install.no': 'Ahora no',

    /* ── El armazón ────────────────────────────────────────────────────── */
    'nav.primary': 'Navegación principal',
    'nav.hunt': 'Búsqueda',
    'nav.board': 'Tablero',
    'nav.add': 'Añadir',
    'nav.map': 'Mapa',
    'nav.me': 'Yo',

    'shell.search': 'Buscar',
    'shell.alerts': 'Alertas',
    'shell.back': 'Atrás',
    'shell.unread': 'sin leer',
    'shell.account': 'Cuenta',
    'shell.language': 'Idioma',
    'shell.skipToContent': 'Ir al contenido',
    'shell.notFound': 'Esa página no existe.',
    'shell.notFoundBody': 'El enlace puede estar viejo, o la oportunidad se combinó con otra.',
    'shell.goHunt': 'Volver a la búsqueda',
    'shell.pending': 'Todavía no está construida',
    'shell.pendingBody': 'Esta pantalla llega con la unidad de pantallas. El marco a su alrededor ya está listo.',

    'route.opportunity': 'Oportunidad',
    'route.inspect': 'Inspección',
    'route.compare': 'Comparar',
    'route.intake': 'Añadir un prospecto',
    'route.setup': 'Configuración',
    'route.settings': 'Ajustes',

    /* ── Encabezados de página ────────────────────────────────────────── */
    'page.eyebrowPipeline': 'Cartera',
    'page.eyebrowGround': 'En el terreno',
    'page.eyebrowDecide': 'Decidiendo',
    'page.eyebrowCapture': 'Captura',
    'page.eyebrowFind': 'Buscar',
    'page.eyebrowWatching': 'Mientras no estabas',
    'page.eyebrowYours': 'Tuyo',
    'page.boardLede': 'Cada prospecto que trabajas, en la columna de la etapa que alcanzó.',
    'page.mapLede': 'Dónde está el terreno de verdad. El color del marcador es la puntuación.',
    'page.compareLede': 'De dos a cuatro fincas lado a lado, en los números que deciden.',
    'page.inspectLede': 'La lista de cotejo que corres parado en la finca. Sigue funcionando sin señal.',
    'page.intakeLede': 'Pega un enlace, o escribe lo que tienes delante. Menos de un minuto en el portón.',
    'page.inboxLede': 'Bajadas de precio, anuncios retirados, fechas que vencen, prospectos callados.',
    'page.settingsLede': 'Todo lo que esta aplicación te puede enseñar es un interruptor aquí. Cada uno dice qué hace.',
  },
} as const

export type StringKey = keyof (typeof STRINGS)['en']

let locale: Locale = detect()

function detect(): Locale {
  // This runs at module scope, so a throw here blanks the whole app. A browser
  // set to block site data throws on the *accessor*, not on the value.
  try {
    const stored = localStorage.getItem('locale')
    if (stored === 'en' || stored === 'es-PR') return stored
  } catch {
    /* fall through to the browser's own preference */
  }
  return navigator.language.toLowerCase().startsWith('es') ? 'es-PR' : 'en'
}

export function getLocale(): Locale {
  return locale
}

/**
 * ── The reactivity fix ───────────────────────────────────────────────────────
 *
 * `setLocale()` used to mutate a module variable and re-render nothing, so the
 * language switch had to reload the page to take effect. "Switch the language,
 * then reload the app" is not shipping Spanish, and Puerto Rican Spanish is a
 * first-class requirement here, not a nice-to-have.
 *
 * Fifteen lines and no dependency: a listener set, a `subscribe`, and a
 * `useLocale()` over React 19's `useSyncExternalStore`. The shell calls
 * `useLocale()` once at the root and every component keeps calling `t(key)`
 * during render exactly as before — so `t()`'s signature does not change and no
 * existing call site moves.
 */
const listeners = new Set<() => void>()

export function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export function setLocale(next: Locale): void {
  locale = next
  try {
    localStorage.setItem('locale', next)
  } catch {
    /* A private window refuses. The choice still applies to this session. */
  }
  // Screen readers and hyphenation both read this, so it is not decorative.
  document.documentElement.lang = next
  for (const fn of [...listeners]) fn()
}

/**
 * Subscribes the calling component to locale changes. The snapshot is the
 * locale STRING, so `Object.is` settles it and a re-render only happens when
 * the language actually changed.
 */
export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, getLocale)
}

/**
 * A missing translation falls back to English rather than rendering the key.
 * A user seeing `account.passkeys` learns nothing; a user seeing the English
 * learns what the control does.
 */
export function t(key: StringKey): string {
  return STRINGS[locale][key] ?? STRINGS.en[key] ?? key
}
